export type BannedUserRecord = {
	id?: number;
	user_id?: number;
	is_active?: boolean | null;
	is_banned?: boolean | null;
	reason_ban?: string | null;
	date_unban?: string | null;
};

type UserWithBanState = {
	is_active?: boolean | null;
	banned_users?: BannedUserRecord[] | null;
};

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null;
}

function isBannedRecord(value: unknown): value is BannedUserRecord {
	return (
		isRecord(value) &&
		("user_id" in value || "is_active" in value || "is_banned" in value)
	);
}

export function extractBannedUsersCollection(payload: unknown): BannedUserRecord[] {
	if (Array.isArray(payload)) {
		return payload.filter(isBannedRecord);
	}

	if (!isRecord(payload)) return [];

	const preferredKeys = ["banned_users", "banned_userses", "items", "banned_user"];
	for (const key of preferredKeys) {
		const candidate = payload[key];
		if (Array.isArray(candidate)) {
			const records = candidate.filter(isBannedRecord);
			if (records.length > 0) return records;
		}
	}

	for (const value of Object.values(payload)) {
		if (!Array.isArray(value)) continue;
		const records = value.filter(isBannedRecord);
		if (records.length > 0) return records;
	}

	return [];
}

export function extractBannedUserRecord(payload: unknown): BannedUserRecord | null {
	if (isBannedRecord(payload)) return payload;
	if (!isRecord(payload)) return null;

	const preferredKeys = ["banned_user", "banned_users", "item", "data"];
	for (const key of preferredKeys) {
		const value = payload[key];
		if (isBannedRecord(value)) return value;
		if (Array.isArray(value)) {
			const first = value.find(isBannedRecord);
			if (first) return first;
		}
	}

	for (const value of Object.values(payload)) {
		if (isBannedRecord(value)) return value;
	}

	return null;
}

export function getBanRecord(
	user?: UserWithBanState | null,
): BannedUserRecord | undefined {
	if (!user || !Array.isArray(user.banned_users) || user.banned_users.length === 0) {
		return undefined;
	}

	return (
		user.banned_users.find((record) => typeof record?.is_active === "boolean") ??
		user.banned_users[0]
	);
}

export function getUserIsActive(user?: UserWithBanState | null): boolean {
	const banRecord = getBanRecord(user);
	if (typeof banRecord?.is_active === "boolean") {
		return banRecord.is_active;
	}

	if (typeof user?.is_active === "boolean") {
		return user.is_active;
	}

	return true;
}

export function getUserStatusLabel(
	user?: UserWithBanState | null,
): "Activo" | "Suspendido" {
	return getUserIsActive(user) ? "Activo" : "Suspendido";
}
